import { ajvResolver } from '@hookform/resolvers/ajv';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { Box } from '@mui/material';
import { JSONSchemaType } from 'ajv';
import { get, merge } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { SidebarAccordion } from 'src/components/accordion';
import { AllowAdsToggle } from 'src/components/allowAdsToggle/AllowAdsToggle';
import { useEditComponentPageContext } from 'src/components/context/components/EditComponentPageContext';
import { Drawer } from 'src/components/drawer';
import { SidebarHeader } from 'src/components/header/sidebarHeader';
import { useGetAllAssets } from 'src/hooks';
import { useEvent } from 'src/hooks/useEvent';
import { DynamicSectionEvents } from 'src/interfaces';
import { lang } from 'src/utils';

import { isNonEmptyString } from '../../EditComponentPage.helper';
import { FormWrapper } from '../FormWrapper';
import { useCODThankYouForm } from './CODThankYouForm.hook';
import {
  CodThankYouComponentConfigSchema,
  CODThankYouFormProps,
  CustomerPath,
  CustomerTypes,
  ICodThankYouConfig,
} from './CODThankYouForm.interface';
import {
  getBackgroundImageItem,
  getNavigationItem,
  getUserItems,
} from './CodThankyouForm-utils';
import { CodThankyouKnownMop } from './CodThankyouKnownMop';
import { KNOWN_USER_TYPES } from './constants';
import { BackgroundFormController } from './controller/backgroundController/BackgroundController';
import { ImageSectionController } from './controller/imageSectionController/ImageSectionController';
import { MessagingController } from './controller/messagingController/MessagingController';

export const CODThankYouForm = (props: CODThankYouFormProps) => {
  const showPlaceholderRef = useRef(false);
  const [activeCustomer, setActiveCustomer] = useState<CustomerPath>(
    CustomerTypes.UNKNOWN,
  );
  const [activeTab, setActiveTab] = useState(KNOWN_USER_TYPES.knownUser);
  const [activeAdsTab, setActiveAdsTab] = useState(KNOWN_USER_TYPES.knownUser);
  const { setIsCustomerKnown } = useEditComponentPageContext();
  const { triggerEvent } = useEvent(
    DynamicSectionEvents.COD_THANK_YOU_OPEN,
    {},
  );
  const {
    data: assets = [],
    isLoading: isLoadingAssets,
    refetch: refetchAssets,
  } = useGetAllAssets();

  const {
    control,
    handleSubmit: submitWrapper,
    reset,
    formState,
    getValues,
    watch,
    setValue,
  } = useForm<ICodThankYouConfig>({
    values: merge(
      {},
      { ads: { showPlaceholder: showPlaceholderRef.current } },
      props.componentDetails.config ?? {},
    ),
    resolver: ajvResolver(
      CodThankYouComponentConfigSchema as unknown as JSONSchemaType<ICodThankYouConfig>,
    ),
  });
  const {
    openKnownUserDrawer,
    openUnknownUserDrawer,
    openAdsConfigurationDrawer,
    openAdsKnownUserDrawer,
    openAdsUnknownUserDrawer,
    openBackgroundDrawer,
    onToggleKnownUserDrawer,
    onToggleUnknownUserDrawer,
    onToggleAdsConfigurationDrawer,
    onToggleAdsKnownUserDrawer,
    onToggleAdsUnknownUserDrawer,
    onToggleBackgroundDrawer,
  } = useCODThankYouForm();

  const config = props.componentDetails.config as ICodThankYouConfig;
  const unknownBackground = config?.unknownCustomer?.background;
  const knownBackground = config?.knownCustomer?.background;
  const mopBackground = config?.knownCustomer?.mop?.background;
  const adsUnknownBackground =
    config?.ads?.content?.unknownCustomer?.background;
  const adsKnownBackground = config?.ads?.content?.knownCustomer?.background;
  const adsMopBackground = config?.ads?.content?.knownCustomer?.mop?.background;

  const customer = activeCustomer;
  const customerErrors = get(formState.errors, customer) || undefined;
  const isAdsEnabled = Boolean(watch('ads.isAdsEnabled'));
  const customerMop = watch(`${customer}.mop` as const);
  const isMopEnabled =
    customerMop !== undefined && customerMop.background !== undefined;

  const [mopJustEnabled, setMopJustEnabled] = useState(false);
  const [adsJustEnabled, setAdsJustEnabled] = useState(false);

  useEffect(() => {
    setMopJustEnabled(false);
    setAdsJustEnabled(false);
  }, [props.saveToken]);

  useEffect(() => {
    if (setIsCustomerKnown) {
      const isKnown =
        activeCustomer === CustomerTypes.KNOWN ||
        activeCustomer === 'ads.content.knownCustomer';
      setIsCustomerKnown(isKnown);
    }
  }, [activeCustomer, setIsCustomerKnown]);

  const hasTriedSaving = !!props.hasTriedSaving;
  const mopBgHasError =
    !mopJustEnabled &&
    mopBackground != null &&
    !isNonEmptyString(mopBackground);
  const adsMopBgHasError =
    !mopJustEnabled &&
    adsMopBackground != null &&
    !isNonEmptyString(adsMopBackground);

  const handleSubmit: SubmitHandler<ICodThankYouConfig> = (value: any) => {
    const { ads, ...rest } = value;
    const persistedAds = ads ? { ...ads } : undefined;

    if (persistedAds) {
      delete persistedAds.showPlaceholder;
    }

    props.onSave({
      ...rest,
      ...(persistedAds
        ? {
            ads: persistedAds,
          }
        : {}),
    });
  };

  const handleOnChange = (key: string, value: unknown, fieldOnChange?: any) => {
    if (key.endsWith('.mop')) {
      setMopJustEnabled(value !== undefined);
    }
    props.onChange(props.componentDetails.config ?? {}, key, value);
    fieldOnChange?.(value);
  };

  const handleOnChangeForAds = (key: string, value: unknown) => {
    if (key === 'ads.isAdsEnabled') {
      setAdsJustEnabled(value === true);
    }
    handleOnChange(key as string, value);
  };

  const setAdsShowPlaceholder = (value: boolean) => {
    handleOnChangeForAds('ads.showPlaceholder', value);
    setValue('ads.showPlaceholder', value, {
      shouldDirty: true,
    });
    showPlaceholderRef.current = value;
  };

  const openAdsConfiguration = () => {
    setAdsShowPlaceholder(true);
    onToggleAdsConfigurationDrawer();
  };

  const closeAdsConfiguration = () => {
    setAdsShowPlaceholder(false);
    onToggleAdsConfigurationDrawer();
  };

  const triggerCustomerEvent = (isCustomerKnown?: boolean, isMop?: boolean) => {
    triggerEvent({
      codData: {
        isCustomerKnown,
        isMop,
      },
    });
  };

  const openCustomerDrawer = (
    drawerToggle: () => void,
    customerPath: CustomerPath,
    isCustomerKnown?: boolean,
    isMop?: boolean,
  ) => {
    setActiveCustomer(customerPath);
    drawerToggle();

    if (isCustomerKnown !== undefined) {
      triggerCustomerEvent(isCustomerKnown, isMop);
    }
  };

  const currentBackground =
    customer === CustomerTypes.KNOWN ? knownBackground : unknownBackground;

  const adsCurrentBackground =
    customer === 'ads.content.knownCustomer'
      ? adsKnownBackground
      : adsUnknownBackground;

  const items = [
    getBackgroundImageItem(
      onToggleBackgroundDrawer,
      hasTriedSaving && !isNonEmptyString(currentBackground),
    ),
    {
      label: lang.thankYouComponent.imageSection,
      content: (
        <ImageSectionController
          control={control}
          customer={customer}
          onChange={handleOnChange}
          errors={customerErrors}
          watch={watch}
        />
      ),
      hasError: !!customerErrors?.media,
    },
    {
      label: lang.thankYouComponent.messaging,
      content: (
        <MessagingController
          control={control}
          customer={customer}
          onChange={handleOnChange}
          errors={customerErrors}
          theme={props.componentDetails.theme}
        />
      ),
      hasError: !!customerErrors?.label,
    },
  ];

  const adsItems = [
    getBackgroundImageItem(
      onToggleBackgroundDrawer,
      !adsJustEnabled &&
        hasTriedSaving &&
        !isNonEmptyString(adsCurrentBackground),
    ),
    ...items.slice(1),
  ];

  const adsDrawerItems = [
    {
      label: lang.allowAds,
      content: <div />,
      chevronIcon: (
        <AllowAdsToggle
          control={control}
          handleOnChange={handleOnChangeForAds}
          setValue={setValue}
          showEyeIconButton={false}
        />
      ),
      disabledExpand: true,
      hasError: false,
    },
    getNavigationItem(
      lang.thankYouComponent.unknownUserWithAds,
      isAdsEnabled
        ? () =>
            openCustomerDrawer(
              onToggleAdsUnknownUserDrawer,
              'ads.content.unknownCustomer',
              false,
            )
        : undefined,
      isAdsEnabled &&
        !adsJustEnabled &&
        hasTriedSaving &&
        !isNonEmptyString(adsUnknownBackground),
      !isAdsEnabled,
    ),
    getNavigationItem(
      lang.thankYouComponent.knownUserWithAds,
      isAdsEnabled
        ? () =>
            openCustomerDrawer(
              onToggleAdsKnownUserDrawer,
              'ads.content.knownCustomer',
              true,
              activeAdsTab === KNOWN_USER_TYPES.mopKnownUser,
            )
        : undefined,
      isAdsEnabled &&
        !adsJustEnabled &&
        ((hasTriedSaving && !isNonEmptyString(adsKnownBackground)) ||
          adsMopBgHasError),
      !isAdsEnabled,
    ),
  ];

  const customerDrawers = [
    {
      open: openKnownUserDrawer,
      onToggle: onToggleKnownUserDrawer,
      label: lang.thankYouComponent.knownUser,
      content: (
        <CodThankyouKnownMop
          items={items}
          backgroundItem={getBackgroundImageItem(
            onToggleBackgroundDrawer,
            hasTriedSaving && mopBgHasError,
          )}
          handleOnChange={handleOnChange}
          customerPath={CustomerTypes.KNOWN}
          isMopEnabled={isMopEnabled}
          activeTab={activeTab}
          onChangeActiveTab={(tab: KNOWN_USER_TYPES) => {
            triggerCustomerEvent(true, tab === KNOWN_USER_TYPES.mopKnownUser);
            setActiveTab(tab);
          }}
        />
      ),
    },
    {
      open: openUnknownUserDrawer,
      onToggle: onToggleUnknownUserDrawer,
      label: lang.thankYouComponent.unknownUser,
      content: <SidebarAccordion items={items} />,
    },
    {
      open: openAdsKnownUserDrawer,
      onToggle: onToggleAdsKnownUserDrawer,
      label: lang.thankYouComponent.knownUserWithAds,
      content: (
        <CodThankyouKnownMop
          items={adsItems}
          backgroundItem={getBackgroundImageItem(
            onToggleBackgroundDrawer,
            hasTriedSaving && adsMopBgHasError,
          )}
          handleOnChange={handleOnChange}
          customerPath="ads.content.knownCustomer"
          isMopEnabled={isMopEnabled}
          activeTab={activeAdsTab}
          onChangeActiveTab={(tab: KNOWN_USER_TYPES) => {
            triggerCustomerEvent(true, tab === KNOWN_USER_TYPES.mopKnownUser);
            setActiveAdsTab(tab);
          }}
        />
      ),
    },
    {
      open: openAdsUnknownUserDrawer,
      onToggle: onToggleAdsUnknownUserDrawer,
      label: lang.thankYouComponent.unknownUserWithAds,
      content: <SidebarAccordion items={adsItems} />,
    },
  ];

  const sideBarItems = [
    {
      label: lang.thankYouComponent.adConfiguration,
      onLabelClick: openAdsConfiguration,
      content: <div />,
      chevronIcon: (
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          mr={-0.5}
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}>
          <ArrowForwardIcon
            onClick={(event) => {
              event.stopPropagation();
              openAdsConfiguration();
            }}
          />
        </Box>
      ),
      disabledExpand: true,
      hasError: Boolean(
        !adsJustEnabled &&
          isAdsEnabled &&
          (get(formState.errors, 'ads.content.knownCustomer') ||
            get(formState.errors, 'ads.content.unknownCustomer') ||
            (hasTriedSaving &&
              (!isNonEmptyString(adsUnknownBackground) ||
                !isNonEmptyString(adsKnownBackground))) ||
            adsMopBgHasError),
      ),
    },
    ...getUserItems(
      hasTriedSaving && (!isNonEmptyString(knownBackground) || mopBgHasError),
      () => {
        openCustomerDrawer(
          onToggleKnownUserDrawer,
          CustomerTypes.KNOWN,
          true,
          activeTab === KNOWN_USER_TYPES.mopKnownUser,
        );
      },
      hasTriedSaving && !isNonEmptyString(unknownBackground),
      () => {
        openCustomerDrawer(
          onToggleUnknownUserDrawer,
          CustomerTypes.UNKNOWN,
          false,
        );
      },
    ),
  ];

  useEffect(() => {
    if (
      props.componentDetails?.config?.ads?.showPlaceholder === undefined &&
      props.componentDetails?.config?.ads?.showPlaceholder !==
        showPlaceholderRef.current
    ) {
      handleOnChange('ads.showPlaceholder', showPlaceholderRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.componentDetails?.config?.ads?.showPlaceholder]);

  return (
    <FormWrapper
      formState={formState}
      formReset={reset}
      formGetValues={getValues}
      onSave={submitWrapper(handleSubmit)}>
      <SidebarAccordion items={sideBarItems} />

      {customerDrawers.map(({ open, label, onToggle, content }) => (
        <Drawer open={open} key={label}>
          <SidebarHeader label={label} onBackClick={onToggle} />
          {content}
        </Drawer>
      ))}

      <Drawer open={openAdsConfigurationDrawer}>
        <SidebarHeader
          label={lang.thankYouComponent.adConfiguration}
          onBackClick={closeAdsConfiguration}
        />
        <SidebarAccordion items={adsDrawerItems} />
      </Drawer>

      <Drawer open={openBackgroundDrawer}>
        {openBackgroundDrawer && (
          <BackgroundFormController
            onToggleBackgroundDrawer={onToggleBackgroundDrawer}
            customer={customer}
            control={control}
            onChange={handleOnChange}
            componentDetails={props.componentDetails}
            assets={assets}
            loading={isLoadingAssets}
            refetchAssets={refetchAssets}
            isMop={
              customer === CustomerTypes.KNOWN
                ? activeTab === KNOWN_USER_TYPES.mopKnownUser
                : customer === 'ads.content.knownCustomer' &&
                  activeAdsTab === KNOWN_USER_TYPES.mopKnownUser
            }
          />
        )}
      </Drawer>
    </FormWrapper>
  );
};
